import { ModuleWithProviders, NgModule } from '@angular/core';
import { LoggerConfiguration, LogsInitConfiguration } from '@datadog/browser-logs';

import { environment } from '@ninety/ui/web/environments';

import { DatadogLogger } from './datadog-logger';
import { DatadogLoggerFactory } from './datadog-logger-factory';
import { DatadogLogsService } from './datadog-logs.service';
import { DatadogReservedTagsRequired } from './types';

type RootOptions = {
  name?: string;
  globalContext?: Partial<
    DatadogReservedTagsRequired & {
      /** Whether the environment is local or in a deployed instance */
      isLocal: boolean;
    }
  >;
  initConf?: Partial<LogsInitConfiguration>;
  conf?: LoggerConfiguration;
};

type ResolvedRootOptions = {
  name: string;
  globalContext: DatadogReservedTagsRequired & {
    /** Whether the environment is local or in a deployed instance */
    isLocal: boolean;
  };
  initConf: LogsInitConfiguration;
  conf?: LoggerConfiguration;
};

type ChildOptions = {
  name: string;
  conf?: LoggerConfiguration;
};

/**
 * Third-party analytics domains whose "Failed to fetch" errors are safe to filter out.
 * These errors are caused by ad blockers or network issues blocking outbound analytics requests.
 */
const THIRD_PARTY_ANALYTICS_DOMAINS = ['segment.com', 'segment.io', 'fullstory.com', 'aptrinsic.com'];
const FAILED_TO_FETCH = 'Failed to fetch';

function getDefaultRootOptions(options: RootOptions = {}): ResolvedRootOptions {
  return {
    name: options.name ?? 'AppModule',
    conf: options.conf,
    globalContext: {
      env: `${environment.env}:${environment.region}`,
      isLocal: environment.hostedEnvironment === 'localhost',
      service: environment.service,
      version: environment.ninetyReleaseVersion ?? environment.appVersion,
      ...options.globalContext,
    },
    initConf: {
      // Return false to omit log
      // Mutate to modify, see redact example
      beforeSend: log => {
        /** DO NOT LOG TO CONSOLE IN THIS METHOD, IT WILL CAUSE AN INFINITE LOOP */
        if (!environment.production) {
          if (log.message?.startsWith('Angular is running in development mode')) {
            return false;
          }
          if (log.message?.startsWith('@ngrx/store: The feature name')) {
            return false;
          }
          if (log.message?.includes('[webpack-dev-server]')) {
            return false;
          }
        }

        // Filter "Failed to fetch" errors originating from third-party analytics scripts.
        // These are caused by ad blockers or network issues blocking requests to analytics
        // domains and are not actionable application errors.
        // Note: The message format can vary (e.g. "Failed to fetch", Failed to fetch,
        // TypeError: Failed to fetch) depending on how the browser/Datadog SDK surfaces it,
        // so we use a contains check rather than an exact match.
        const isFailedToFetch = [log.message, log.error?.message].some(
          m => typeof m === 'string' && m.includes(FAILED_TO_FETCH)
        );
        if (
          log.origin === 'console' &&
          isFailedToFetch &&
          THIRD_PARTY_ANALYTICS_DOMAINS.some(domain => log.error?.stack?.includes(domain))
        ) {
          return false;
        }

        // We want to use level for consistency
        log.level = log.status;
        delete log.status;
        // Eg. of redaction
        // log.view.url = log.view.url.replace(/email=[^&]*/, 'email=REDACTED');

        // v5+ requires an explicit boolean: true to send the log, false to discard
        return true;
      },
      clientToken: environment.dataDogClientToken,
      env: environment.env,
      forwardConsoleLogs: 'all',
      forwardErrorsToLogs: true,
      sessionSampleRate: 100,
      site: 'datadoghq.com',
      // opt out of telemetry
      telemetrySampleRate: 0,
      version: environment.ninetyReleaseVersion ?? environment.appVersion,
      ...options.initConf,
    },
  };
}

@NgModule()
export class DatadogLoggerModule {
  static forRoot(options: RootOptions = {}): ModuleWithProviders<DatadogLoggerModule> {
    const rootOptions = getDefaultRootOptions(options);

    return {
      ngModule: DatadogLoggerModule,
      providers: [
        {
          provide: DatadogLogger,
          useFactory: () => DatadogLoggerFactory(rootOptions.name, rootOptions.conf),
        },
        {
          provide: DatadogLogsService,
          useFactory: () => {
            const service = new DatadogLogsService().init(rootOptions.initConf);
            service.setGlobalContext(rootOptions.globalContext);

            return service;
          },
        },
      ],
    };
  }

  static forFeature(options: ChildOptions): ModuleWithProviders<DatadogLoggerModule> {
    return {
      ngModule: DatadogLoggerModule,
      providers: [
        {
          provide: DatadogLogger,
          useFactory: () => DatadogLoggerFactory(options.name, options.conf),
        },
      ],
    };
  }
}
